import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Badge, DropDown } from "nlib/ui";
import { DropDownContent } from "nlib/ui/DropDown";
import {
  getContactsData,
  getVendorsCategories,
  getVendorsClasses,
  getVendorsLocations,
  getVendorsProjects,
  getVendorsTaxRates
} from "selectors/contacts";
import {
  getSelectedBusinessCategories,
  getSelectedBusinessClasses,
  getSelectedBusinessLocations,
  getSelectedBusinessProjects,
  getSelectedBusinessTaxRates
} from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import Arrays from "utils/Arrays";
import ConfidenceBadge from "../ConfidenceBadge";
import Constants from "const/Constants";
import ContactsActions from "actions/ContactsActions";
import React, { useCallback, useMemo, useState } from "react";
import Texts from "utils/Texts";
import UiActions from "actions/UiActions";
import classNames from "classnames";
import moment from "moment";

const MAX_VALIDATION_ITEMS_TO_SHOW = 5;

const LOW_LEVEL = 10;

const MEDIUM_LEVEL = 50;

const IconWarning = () => {
  return (
    <Badge className={Css.badge} theme="warning">
      <Icons.Warning />
    </Badge>
  );
};

const IconCriticalWarning = () => {
  return (
    <Badge className={Css.badge} theme="negative">
      <Icons.Warning />
    </Badge>
  );
};

const Link = (props) => {
  const { title, value, current, frequency = null, lastSelectionDate, onClick } = props;

  const { uiTexts } = useSelector(getTextsData);

  const clickable = !!title && !current && !!onClick;

  const labelClassName = useMemo(() => {
    if (!title) return Css.mediumDarkText;
    if (+frequency < LOW_LEVEL) return Css.negativeText;
    if (+frequency < MEDIUM_LEVEL) return Css.warningText;

    return Css.positiveText;
  }, [frequency, title]);

  const badgeTheme = useMemo(() => {
    if (!title) return undefined;
    if (+frequency < LOW_LEVEL) return "negative";
    if (+frequency < MEDIUM_LEVEL) return "warning";

    return "positive";
  }, [frequency, title]);

  const handleClick = useCallback(() => onClick(value), [onClick, value]);

  const lastSelectedText = useMemo(() => {
    return lastSelectionDate
      ? `${uiTexts.lastUsed}: ${moment.duration(moment.utc(lastSelectionDate).diff(moment.utc())).humanize(true)}`
      : null;
  }, [lastSelectionDate, uiTexts.lastUsed]);

  return (
    <div
      className={classNames(
        Css.link,
        !clickable && Css.selected,
        lastSelectedText && Css.lastSelected
      )}
      onClick={clickable ? handleClick : undefined}>
      <span className={classNames(Css.label, labelClassName)} />
      <span title={title} className={Css.text}>{title || uiTexts.unknown}</span>
      {lastSelectedText && (
        <span
          className={Css.lastSelectionDate}
          data-tooltip={lastSelectedText}
          data-tooltip-position="left">
          <Icons.Clock />
        </span>
      )}
      {frequency !== null && (
        <Badge
          theme={badgeTheme}
          className={Css.frequency}>
          {` ${frequency || "<1"}%`}
        </Badge>
      )}
    </div>
  );
};

const Links = (props) => {
  const {
    idProp,
    idsList,
    entityId,
    nameProp,
    moreCount,
    lastUsedId,
    entitiesList,
    lastSelectionDate,
    onLinkClick
  } = props;

  const currentIndex = idsList?.length ? idsList.findIndex(([id]) => id === entityId) : -1;

  const listItems = moreCount
    ? idsList.filter(([id], index) => {
      return currentIndex < MAX_VALIDATION_ITEMS_TO_SHOW
        ? index < MAX_VALIDATION_ITEMS_TO_SHOW
        : entityId === id || (index < MAX_VALIDATION_ITEMS_TO_SHOW - 1);
    })
    : idsList;

  return listItems.map(([id, , usageFrequency]) => {
    return (
      <Link
        key={id}
        value={id}
        current={id === entityId}
        lastSelectionDate={id === lastUsedId ? lastSelectionDate : undefined}
        title={Arrays.findByKey(entitiesList, idProp, id)?.[nameProp]}
        frequency={Math.round(usageFrequency * Constants.PERCENTS_MULTIPLIER)}
        onClick={onLinkClick} />
    );
  });
};

const ENTITIES_SELECTORS = {
  categories: getSelectedBusinessCategories,
  classes: getSelectedBusinessClasses,
  locations: getSelectedBusinessLocations,
  projects: getSelectedBusinessProjects,
  taxRates: getSelectedBusinessTaxRates
};

const VENDORS_ENTITIES_SELECTORS = {
  categories: getVendorsCategories,
  classes: getVendorsClasses,
  locations: getVendorsLocations,
  projects: getVendorsProjects,
  taxRates: getVendorsTaxRates
};

const TEXT_TEMPLATE_LABELS = {
  categories: "vendorsCategoriesValidationWarning",
  classes: "vendorsClassesValidationWarning",
  locations: "vendorsLocationsValidationWarning",
  projects: "vendorsProjectsValidationWarning",
  taxRates: "vendorsTaxRatesValidationWarning"
};

const VendorValidation = (props) => {
  const [openedState, setOpenedState] = useState(false);

  const {
    skip,
    opened = openedState,
    children,
    vendorId,
    listName,
    name,
    idProp = "id",
    nameProp = "name",
    entityValue: { [idProp]: entityId, [nameProp]: entityName },
    confidence,
    setOpened = setOpenedState,
    updateTransaction
  } = props;

  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const contactsData = useSelector(getContactsData);

  const contactData = Arrays.findById(contactsData, vendorId);

  const entitiesList = useSelector(ENTITIES_SELECTORS[listName]);

  const vendorEntities = useSelector(VENDORS_ENTITIES_SELECTORS[listName])?.[vendorId];

  const idsProp = name === "category" ? "categoryCodes" : `${name}Ids`;

  const {
    idsList,
    moreCount,
    lastUsedId,
    mostUsedId,
    showWarning,
    lastSelectionDate,
    showCriticalWarning
  } = useMemo(() => {
    if (skip) return {};

    const mostUsedIdProp = name === "category" ? "mostUsedCode" : "mostUsedId";

    const lastUsedIdProp = name === "category" ? "lastUsedCode" : "lastUsedId";

    const {
      [idsProp]: ids,
      [lastUsedIdProp]: lastUsedEntityId,
      [mostUsedIdProp]: mostUsedEntityId,
      lastCategorizedAt,
      lastClassSelectedAt,
      lastLocationSelectedAt,
      lastProjectSelectedAt,
      lastTaxRateSelectedAt
    } = vendorEntities || {};

    const show = !!ids?.length && entityId && entityId !== mostUsedEntityId && entityId !== lastUsedEntityId;

    return {
      idsList: ids,
      showWarning: show,
      lastUsedId: lastUsedEntityId,
      mostUsedId: mostUsedEntityId,
      moreCount: show && ids?.length && Math.max(ids?.length - MAX_VALIDATION_ITEMS_TO_SHOW, 0),
      lastSelectionDate: lastCategorizedAt || lastClassSelectedAt || lastLocationSelectedAt || lastProjectSelectedAt
        || lastTaxRateSelectedAt,
      showCriticalWarning: show && ids.every(([id]) => id !== entityId)
    };
  }, [entityId, idsProp, name, skip, vendorEntities]);

  const capitalizedName = useMemo(() => Texts.capitalize(contactData?.name), [contactData?.name]);

  const textTemplateLabel = TEXT_TEMPLATE_LABELS[listName];

  const lastUsedEntityName = useMemo(() => {
    return Arrays.findByKey(entitiesList, idProp, lastUsedId)?.[nameProp];
  }, [entitiesList, idProp, lastUsedId, nameProp]);

  const handleLinkClick = useCallback((value) => {
    const newValue = Arrays.findByKey(entitiesList, idProp, value);

    updateTransaction(newValue);
    setOpened(false);
  }, [entitiesList, idProp, updateTransaction, setOpened]);

  const handleStopValidationClick = useCallback(async() => {
    const text = Texts.replaceVars(messages.stopVendorValidation, { contactName: contactData?.name });

    const result = await dispatch(UiActions.showModal(text, null, true));

    if (result) dispatch(ContactsActions.stopVendorsValidation(vendorId));
  }, [messages.stopVendorValidation, contactData?.name, dispatch, vendorId]);

  const [
    vendorValidationWarningA,
    vendorValidationWarningB,
    vendorValidationWarningC,
    vendorValidationWarningD
  ] = useMemo(() => {
    const [textA, textB, textC, textD] = messages[textTemplateLabel];

    if (listName === "projects" && entitiesList.length) {
      const [
        vendorsProjectsAndCustomersValidationWarningA,
        vendorsProjectsAndCustomersValidationWarningB,
        vendorsProjectsAndCustomersValidationWarningC
      ] = messages.vendorsProjectsAndCustomersValidationWarning;

      const [
        vendorsCustomersValidationWarningA,
        vendorsCustomersValidationWarningB,
        vendorsCustomersValidationWarningC
      ] = messages.vendorsCustomersValidationWarning;

      const hasProjects = entitiesList.some(({ customer }) => !customer);

      const hasCustomers = entitiesList.some(({ customer }) => !!customer);

      if (hasProjects && hasCustomers) {
        return [
          vendorsProjectsAndCustomersValidationWarningA,
          vendorsProjectsAndCustomersValidationWarningB,
          textC,
          vendorsProjectsAndCustomersValidationWarningC
        ];
      }
      if (hasCustomers) {
        return [
          vendorsCustomersValidationWarningA,
          vendorsCustomersValidationWarningB,
          textC,
          vendorsCustomersValidationWarningC
        ];
      }
    }

    return [textA, textB, textC, textD];
  }, [messages, textTemplateLabel, listName, entitiesList]);

  const handleMouseEnter = useCallback(() => {
    setOpened(true);
  }, [setOpened]);

  const handleMouseLeave = useCallback(() => {
    setOpened(false);
  }, [setOpened]);

  const iconComponent = useMemo(() => {
    if (!showCriticalWarning && !showWarning) return undefined;

    return (
      <div
        className={Css.icon}
        onMouseEnter={handleMouseEnter}>
        {showCriticalWarning ? <IconCriticalWarning /> : <IconWarning />}
      </div>
    );
  }, [showCriticalWarning, showWarning, handleMouseEnter]);

  return (
    <DropDown
      opened={opened}
      onMouseLeave={handleMouseLeave}
      setOpened={setOpened}>
      {children && children({ showWarning, showCriticalWarning, iconComponent })}
      {showWarning && (
        <DropDownContent alignRight className={Css.dropDown}>
          <div className={classNames(Css.vendorValidation, props.className)}>
            {!!confidence && (
              <div className={Css.confidence}>
                <div>{uiTexts.confidence}</div>
                <ConfidenceBadge className={Css.badge} confidence={confidence} />
              </div>
            )}
            <div className={Css.scroll}>
              <div className={Css.block}>
                <div className={Css.links}>
                  <div className={Css.text}>
                    {`${Texts.replaceVars(vendorValidationWarningA, { contactName: capitalizedName })}: `}
                  </div>
                  <Links
                    name={name}
                    idProp={idProp}
                    idsList={idsList}
                    entityId={entityId}
                    nameProp={nameProp}
                    vendorId={vendorId}
                    moreCount={moreCount}
                    entityName={entityName}
                    entitiesList={entitiesList}
                    lastUsedId={lastUsedId !== mostUsedId && lastUsedId}
                    onLinkClick={handleLinkClick} />
                  {(!!moreCount || showCriticalWarning) && (
                    <div className={Css.text}>
                      {!!moreCount && (
                        <span>
                          {Texts.replaceVars(vendorValidationWarningB, { moreCount }).toLocaleLowerCase()}
                        </span>
                      )}
                      {!!moreCount && showCriticalWarning && <span>, </span>}
                      {showCriticalWarning && (
                        <span>{vendorValidationWarningC.toLocaleLowerCase()}</span>
                      )}
                    </div>
                  )}
                  {showCriticalWarning && <Link critical title={entityName} />}
                  {!!lastUsedEntityName && idsList.length > 1 && (
                    <>
                      <div className={Css.text}>{vendorValidationWarningD}</div>
                      <Link
                        value={lastUsedId}
                        title={lastUsedEntityName}
                        lastSelectionDate={lastSelectionDate}
                        onClick={handleLinkClick} />
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className={Css.description}>
              {messages.vendorsValidationSummary}
            </div>
            <div className={Css.ignoreValidation} onClick={handleStopValidationClick}>
              <Icons.MinusCircle className={Css.negativeText} />
              <span>{uiTexts.ignoreValidation}</span>
            </div>
          </div>
        </DropDownContent>
      )}
    </DropDown>
  );
};

export default React.memo(VendorValidation);
